import React, { useCallback, useState } from 'react';
import { Form } from 'react-final-form';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { BaseModalProps } from 'Components/UIComponents/interfaces/UIModal';
import UIButton from 'Components/UIComponents/UIButton';
import UIModal from 'Components/UIComponents/UIModal';
import {
  useCardAttach,
  useCreateCard,
  useLatestInvoiceGet,
  useUpcomingInvoiceGet,
} from 'Hooks/Billing';
import useIsMobile from 'Hooks/Common/useIsMobile';
import {
  ApiPlan,
  ApiPlanChangePayload,
  ApiPlanTypes,
  CardFormValues,
  CreateSubscriptionCheckoutPayload,
  Plan,
  PlanChangePayload,
  PlanDurations,
  PlanTypes,
  UpcomingInvoiceTypes,
} from 'Interfaces/Billing';
import { capitalize } from 'lodash';
import { UpcomingInvoice } from 'Pages/Settings/Billing/screens/BillingDefaultPlanScreen';
import { DataLayerAnalytics } from 'Services/Integrations';
import Toast from 'Services/Toast';
import { selectGrid } from 'Utils/selectors';

export interface CompleteTrialModalProps extends BaseModalProps {
  targetPlan: Plan | ApiPlan;
  sourcePlan: Plan | ApiPlan;
  cardInitialValues: CardFormValues | undefined;
  appliedCouponId?: string;
  isLoading?: boolean;
  onChangePlan: (payload: PlanChangePayload | ApiPlanChangePayload) => Promise<void>;
  onPromoAdd: () => void;
  onPromoClear: () => void;
  header?: string;
}

const CompleteTrialModal = ({
  targetPlan,
  sourcePlan,
  cardInitialValues,
  appliedCouponId,
  isLoading,
  onChangePlan,
  onPromoAdd,
  onPromoClear,
  onClose,
  header = 'Upgrade your plan',
}: CompleteTrialModalProps) => {
  const [createCard] = useCreateCard();
  const [attachCard] = useCardAttach();
  const [getLatestInvoice] = useLatestInvoiceGet();
  const [getUpcomingInvoice, isLoadingUpcomingInvoice] = useUpcomingInvoiceGet();

  const [upcomingInvoice, setUpcomingInvoice] = useState<UpcomingInvoiceTypes>();
  const isMobile = useIsMobile();
  const isApiPlan = Object.values(ApiPlanTypes).includes(targetPlan.type as ApiPlanTypes);

  const grid = useSelector(selectGrid);

  const handleChangePlan = useCallback(
    async (values: CardFormValues) => {
      try {
        if (!cardInitialValues) {
          const cardPayload = await createCard(values);
          cardPayload && (await attachCard(cardPayload));
        }

        await onChangePlan({
          type: targetPlan.type,
          duration: targetPlan.duration,
          trial: true,
        });

        const latestInvoice = await getLatestInvoice(undefined);

        DataLayerAnalytics.firePurchaseEvent({
          //@ts-ignore
          transaction_id: latestInvoice?.transactionId,
          //@ts-ignore
          previous_plan_name: sourcePlan.title,
          count_of_docs_saved: grid?.length,
          item: {
            item_id: targetPlan.id,
            item_name: targetPlan.title,
            currency: upcomingInvoice?.currency,
            discount: upcomingInvoice?.discount,
            item_category: 'Plan',
            item_list_id: 'plans',
            item_list_name: 'Plans',
            item_variant: upcomingInvoice?.plan?.duration,
            price: upcomingInvoice?.plan?.price,
            quantity: upcomingInvoice?.quantity,
          },
        });

        onClose();
      } catch (error) {
        Toast.handleErrors(error);
      }
    },
    [
      attachCard,
      cardInitialValues,
      createCard,
      getLatestInvoice,
      grid,
      onChangePlan,
      onClose,
      sourcePlan.title,
      targetPlan.duration,
      targetPlan.id,
      targetPlan.title,
      targetPlan.type,
      upcomingInvoice,
    ],
  );

  const handleGetUpcomingInvoice = useCallback(
    async (values: CreateSubscriptionCheckoutPayload) => {
      return getUpcomingInvoice(values);
    },
    [getUpcomingInvoice],
  );

  return (
    <UIModal onClose={onClose} className="changePlanModal">
      <div className={classNames('billing__plan-modal', { mobile: isMobile })}>
        <Form
          onSubmit={handleChangePlan}
          render={({ handleSubmit }) => (
            <form onSubmit={handleSubmit} className="card-form">
              <div className="billing__plan-modal-title">{header}</div>
              <div className="billing__plan-modal-subtitle">
                <span style={{ color: '#7D8D98' }}>
                  Continue getting your documents signed painlessly with&nbsp;
                  <span style={{ fontWeight: 'bold', color: '#000' }}>
                    your Signaturely {capitalize(targetPlan.type)} Plan
                  </span>
                  .
                </span>
              </div>
              <UpcomingInvoice
                targetPlan={targetPlan}
                sourcePlan={sourcePlan}
                couponId={appliedCouponId}
                onCouponAdd={onPromoAdd}
                onCouponClear={onPromoClear}
                onGetInvoice={setUpcomingInvoice}
                getUpcomingInvoice={handleGetUpcomingInvoice}
                isLoadingUpcomingInvoice={isLoadingUpcomingInvoice}
                isApiPlan={isApiPlan}
              />
              <div
                className={classNames('billing__plan-modal', 'buttons', {
                  left: sourcePlan.type !== PlanTypes.FREE,
                  mobile: isMobile,
                })}
              >
                <UIButton
                  isLoading={isLoading}
                  disabled={isLoading}
                  className={classNames({ mobile: isMobile })}
                  priority="primary"
                  type="submit"
                  title="Complete Trial"
                />
              </div>
            </form>
          )}
        />
        {upcomingInvoice && (
          <div className="billing__plan-modal footer">
            <div>
              Your subscription will be set to auto-renew for $
              {(upcomingInvoice.discountedAmount * upcomingInvoice.quantity).toFixed(2)}
              &nbsp;per&nbsp;
              {targetPlan.duration === PlanDurations.ANNUALLY ? 'year' : 'month'}. You
              also agree to our
              <a
                href="https://signaturely.com/terms/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms of Service
              </a>
              &nbsp;and
              <a
                href="https://signaturely.com/privacy/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </a>
              .
            </div>
          </div>
        )}
      </div>
    </UIModal>
  );
};

export default CompleteTrialModal;
